import React from "react";
import { Heart } from "lucide-react";
import { useWishlist } from "../hooks/useWishlist";
import { useWishlistContext } from "../hooks/useWishlistContext";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

interface WishlistButtonProps {
  productId: number;
  size?: "sm" | "md" | "lg";
  className?: string;
  showText?: boolean;
  isWishlisted?: boolean;
}

const WishlistButton: React.FC<WishlistButtonProps> = ({
  productId,
  size = "md",
  className = "",
  isWishlisted: propIsWishlisted,
}) => {
  const { user } = useAuth();
  const { addToWishlist, removeFromWishlist, isInWishlist, loading } =
    useWishlist();
  const { refreshWishlist: refreshWishlistContext } = useWishlistContext();
  const navigate = useNavigate();

  // Use prop if provided, otherwise fall back to global state
  const inWishlist =
    propIsWishlisted !== undefined ? propIsWishlisted : isInWishlist(productId);
  const [localIsWishlisted, setLocalIsWishlisted] = React.useState(inWishlist);
  const [message, setMessage] = React.useState<string | null>(null);

  // Sync local state when prop or global state changes
  React.useEffect(() => {
    setLocalIsWishlisted(inWishlist);
  }, [inWishlist]);
  const handleToggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate("/registration");
      return;
    }
    try {
      if (localIsWishlisted) {
        const removed = await removeFromWishlist(productId);
        if (removed) {
          setLocalIsWishlisted(false);
          // setMessage("Removed from wishlist");
          // Refresh the context to update other components
          await refreshWishlistContext();
        } else {
          // setMessage("Failed to remove from wishlist");
        }
      } else {
        const added = await addToWishlist(productId);
        if (added) {
          setLocalIsWishlisted(true);
          // setMessage("Added to wishlist");
          // Refresh the context to update other components
          await refreshWishlistContext();
        } else {
          // setMessage("Failed to add to wishlist");
        }
      }
    } catch (err) {
      setMessage("Something went wrong");
    }
    setTimeout(() => setMessage(null), 2000);
  };

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const buttonSizeClasses = {
    sm: "p-1",
    md: "p-2",
    lg: "p-3",
  };

  return (
    <>
      <button
        onClick={handleToggleWishlist}
        disabled={loading}
        className={`
    group flex items-center justify-center rounded-full transition-all duration-300 ease-out
    ${buttonSizeClasses[size]}
    ${
      localIsWishlisted
        ? "bg-gradient-to-r from-red-50 to-pink-50 text-red-600 hover:from-red-100 hover:to-pink-100 ring-2 ring-red-200 hover:ring-red-300 shadow-red-100"
        : "bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 hover:from-red-50 hover:to-pink-50 hover:text-red-600 ring-2 ring-gray-200 hover:ring-red-200 shadow-gray-100"
    }
    ${
      loading
        ? "opacity-50 cursor-not-allowed"
        : "hover:scale-110 hover:shadow-lg active:scale-95"
    }
    shadow-md backdrop-blur-sm
    relative overflow-hidden
    before:absolute before:inset-0 before:bg-white before:opacity-0 
    before:transition-opacity before:duration-300 hover:before:opacity-20
    ${className}
  `}
        title={localIsWishlisted ? "Remove from wishlist" : "Add to wishlist"}
      >
        <Heart
          className={`${sizeClasses[size]} transition-all duration-300 ${
            localIsWishlisted
              ? "fill-current"
              : "group-hover:scale-110 group-hover:animate-pulse"
          }`}
        />
      </button>
      {message && (
        <div className="text-xs text-center mt-1 text-green-600">{message}</div>
      )}
    </>
  );
};

export default WishlistButton;
